import { useEffect, useRef, useState } from "react";
import { Database, Play, Lightning, CheckCircle, ClockCounterClockwise, Trash, ArrowClockwise } from "@phosphor-icons/react";
import { listMigrations, runMigration, runMigrationAsync, getMigrationJob, markMigrationApplied,
  listMigrationBackups, deleteMigrationBackup } from "../lib/api";
import { toast } from "sonner";

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function BackupsList({ onCount }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await listMigrationBackups();
      setRows(data.backups || []);
      setLoaded(true);
      onCount?.((data.backups || []).length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load backups");
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (name) => {
    if (!window.confirm(`Delete backup collection "${name}"?\n\nThis permanently drops the snapshot a migration took. Only do this once you've confirmed that migration is good. Irreversible.`)) return;
    setBusy(name);
    try {
      const { data } = await deleteMigrationBackup(name);
      toast.success(`Deleted ${data.deleted} (${data.docs} docs)`);
      setRows((rs) => {
        const next = rs.filter((r) => r.name !== name);
        onCount?.(next.length);
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy("");
    }
  };

  const totalBytes = rows.reduce((a, r) => a + (r.storage_bytes || 0), 0);

  return (
    <div data-testid="migrations-backups">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] text-zinc-500">{rows.length} backup collection{rows.length === 1 ? "" : "s"} · {fmtBytes(totalBytes)} on disk</span>
        <button onClick={load} className="inline-flex items-center gap-1 h-7 px-2 rounded border border-zinc-300 text-[11px] font-bold text-zinc-600 hover:border-[#6043EF] hover:text-[#6043EF] ml-auto" data-testid="backups-refresh">
          <ArrowClockwise size={12} weight="bold" /> Refresh
        </button>
      </div>
      {!loaded && <div className="text-xs text-zinc-400">Loading…</div>}
      {loaded && rows.length === 0 && <div className="text-xs text-zinc-400" data-testid="backups-empty">No backup collections.</div>}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center justify-between gap-3 border border-zinc-200 rounded-lg px-3 py-2 bg-white" data-testid={`backup-row-${r.name}`}>
            <div className="min-w-0">
              <div className="font-mono text-[12px] text-[#09090B] truncate">{r.name}</div>
              <div className="text-[10px] text-zinc-400 font-mono">{r.count} docs · {fmtBytes(r.storage_bytes ?? r.size_bytes)}</div>
            </div>
            <button onClick={() => del(r.name)} disabled={busy === r.name} data-testid={`backup-delete-${r.name}`}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-rose-300 text-xs font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-50 shrink-0">
              <Trash size={13} weight="bold" /> {busy === r.name ? "Deleting…" : "Delete"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultView({ result }) {
  if (!result) return null;
  const r = result.result || result;
  return (
    <div className="mt-2 rounded-lg bg-zinc-900 text-zinc-100 p-3 font-mono text-[11px] overflow-x-auto" data-testid="migration-result">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {"orphans_found" in r && <span>orphans_found: <b className="text-amber-300">{r.orphans_found}</b></span>}
        {"deleted" in r && r.applied && <span>deleted: <b className="text-rose-300">{r.deleted}</b></span>}
        {r.marked_applied ? <span className="text-emerald-300">MARKED APPLIED</span> : (r.applied ? <span className="text-emerald-300">APPLIED</span> : <span className="text-sky-300">DRY-RUN</span>)}
        {r.backup && <span>backup: <b className="text-emerald-300">{r.backup}</b></span>}
      </div>
      {r.by_body && Object.keys(r.by_body).length > 0 && (
        <div className="mt-2 space-y-0.5">
          {Object.entries(r.by_body).map(([body, cats]) => (
            <div key={body}><span className="text-sky-300">{body}</span> ({cats.length}): <span className="text-zinc-400">{cats.join(", ")}</span></div>
          ))}
        </div>
      )}
      {/* Generic fallback: dump any summary fields the pretty-printer above doesn't
          special-case, so migrations with richer summaries (counts, code lists, plans)
          show their full output instead of a bare badge. */}
      {(() => {
        const HANDLED = new Set(["orphans_found", "deleted", "applied", "marked_applied", "backup", "by_body"]);
        const extra = Object.fromEntries(Object.entries(r).filter(([k]) => !HANDLED.has(k)));
        if (!Object.keys(extra).length) return null;
        return (
          <pre className="mt-2 whitespace-pre-wrap break-words text-[10.5px] leading-snug text-zinc-300 max-h-80 overflow-auto" data-testid="migration-result-json">
            {JSON.stringify(extra, null, 2)}
          </pre>
        );
      })()}
    </div>
  );
}

function MigrationCard({ m, applied, onRan }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState("");
  const pollRef = useRef(null);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const run = async (apply) => {
    if (apply && !window.confirm(`Apply migration "${m.name}"? This writes to the database (a backup is taken).`)) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await runMigration(m.id, apply);
      setResult(data);
      toast.success(apply ? `Applied: ${m.name}` : `Dry-run complete: ${m.name}`);
      onRan?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setRunning(false);
    }
  };

  // Long migrations: run in the background job queue + poll (survives HTTP/ingress timeouts).
  const runBackground = async (apply) => {
    if (apply && !window.confirm(`Apply migration "${m.name}" in the BACKGROUND?\n\nUse this for long (15-20 min) migrations. It runs on the server job queue and keeps going even if you close this page. A backup is taken.`)) return;
    setRunning(true);
    setResult(null);
    setStage("queued…");
    try {
      const { data } = await runMigrationAsync(m.id, apply);
      const jobId = data.job_id;
      toast.success(`Queued in background: ${m.name}`);
      const poll = async () => {
        try {
          const { data: job } = await getMigrationJob(jobId);
          setStage(job.progress?.stage || job.status);
          if (job.status === "completed") {
            setResult(job.result);
            setRunning(false); setStage("");
            toast.success(apply ? `Applied (background): ${m.name}` : `Dry-run done: ${m.name}`);
            onRan?.();
            return;
          }
          if (job.status === "failed" || job.status === "cancelled") {
            setRunning(false); setStage("");
            toast.error(`${job.status}: ${job.error || m.name}`);
            return;
          }
          pollRef.current = setTimeout(poll, 4000);
        } catch {
          pollRef.current = setTimeout(poll, 6000);
        }
      };
      pollRef.current = setTimeout(poll, 2000);
    } catch (e) {
      setRunning(false); setStage("");
      toast.error(e?.response?.data?.detail || "Could not queue migration");
    }
  };

  const markApplied = async () => {
    if (!window.confirm(`Mark migration "${m.name}" as APPLIED?\n\nThis does NOT run the script or touch collection data — it only records the migration as applied. Use this for long / externally-handled migrations you have already resolved manually.`)) return;
    setRunning(true);
    setResult(null);
    try {
      await markMigrationApplied(m.id);
      toast.success(`Marked as applied: ${m.name}`);
      onRan?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as applied");
    } finally {
      setRunning(false);
    }
  };

  const last = m.last_run;
  return (
    <div className="border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`migration-card-${m.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-bold text-sm text-[#09090B] flex items-center gap-1.5">
            {applied && <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />}
            {m.name}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">{m.description}</p>
          {last && (
            <div className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1" data-testid={`migration-lastrun-${m.id}`}>
              <ClockCounterClockwise size={11} /> last: {last.result?.marked_applied ? "marked applied" : (last.apply ? "applied" : "dry-run")} · {new Date(last.at).toLocaleString()}
              {last.result?.deleted != null && last.apply ? ` · deleted ${last.result.deleted}` : ""}
            </div>
          )}
          {running && stage && (
            <div className="text-[11px] text-[#6043EF] font-mono mt-1 flex items-center gap-1.5" data-testid={`migration-stage-${m.id}`}>
              <span className="w-2 h-2 rounded-full bg-[#6043EF] animate-pulse" /> {stage}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <button onClick={() => run(false)} disabled={running} data-testid={`migration-dryrun-${m.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-zinc-300 text-xs font-bold text-zinc-700 hover:border-[#6043EF] hover:text-[#6043EF] disabled:opacity-50">
            <Play size={13} weight="bold" /> Dry-run
          </button>
          <button onClick={() => run(true)} disabled={running} data-testid={`migration-apply-${m.id}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#6043EF] text-white text-xs font-bold hover:bg-[#4f36c9] disabled:opacity-50">
            <Lightning size={13} weight="fill" /> Apply
          </button>
          <button onClick={() => runBackground(true)} disabled={running} data-testid={`migration-apply-bg-${m.id}`}
            title="Run on the server job queue — for long (15-20 min) migrations. Keeps running even if you close the page."
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50">
            <Lightning size={13} weight="fill" /> Apply (long)
          </button>
          {!applied && (
            <button onClick={markApplied} disabled={running} data-testid={`migration-mark-applied-${m.id}`}
              title="Record this migration as applied WITHOUT running the script. For long / externally-handled migrations resolved manually."
              className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-emerald-500 text-xs font-bold text-emerald-600 hover:bg-emerald-50 disabled:opacity-50">
              <CheckCircle size={13} weight="bold" /> Mark as applied
            </button>
          )}
        </div>
      </div>
      <ResultView result={result} />
    </div>
  );
}

export default function MigrationsPanel() {
  const [migrations, setMigrations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("new");
  const [backupCount, setBackupCount] = useState(0);

  const load = async () => {
    try {
      const { data } = await listMigrations();
      setMigrations(data.migrations || []);
      setLoaded(true);
    } catch { /* admin only */ }
  };
  useEffect(() => {
    load();
    listMigrationBackups().then(({ data }) => setBackupCount((data.backups || []).length)).catch(() => {});
  }, []);

  const isApplied = (m) => !!(m.last_run && m.last_run.apply);
  const appliedList = migrations.filter(isApplied);
  const newList = migrations.filter((m) => !isApplied(m));
  const shown = tab === "applied" ? appliedList : newList;

  const TabBtn = ({ id, label, count }) => (
    <button onClick={() => setTab(id)} data-testid={`migrations-tab-${id}`}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-bold transition-colors ${tab === id ? "bg-[#6043EF] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
      {label}
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${tab === id ? "bg-white/20" : "bg-zinc-200 text-zinc-600"}`}>{count}</span>
    </button>
  );

  return (
    <div className="border-2 border-zinc-200 rounded-xl overflow-hidden bg-white mb-5" data-testid="migrations-panel">
      <div className="px-3 py-2 flex items-center gap-3 border-b border-zinc-100 bg-zinc-50 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#09090B]">
          <Database size={14} weight="bold" className="text-[#6043EF]" /> Database Migrations
        </div>
        <span className="text-[11px] font-mono text-zinc-500">{migrations.length} registered</span>
        <span className="text-[10px] text-zinc-400 ml-auto hidden sm:block">idempotent · dry-run first · backed up on apply</span>
      </div>
      <div className="px-3 pt-3 flex items-center gap-2" data-testid="migrations-tabs">
        <TabBtn id="new" label="New" count={newList.length} />
        <TabBtn id="applied" label="Applied" count={appliedList.length} />
        <TabBtn id="backups" label="Backups" count={backupCount} />
      </div>
      <div className="p-3 space-y-2" data-testid="migrations-list">
        {tab === "backups" ? (
          <BackupsList onCount={setBackupCount} />
        ) : (
          <>
            {!loaded && <div className="text-xs text-zinc-400">Loading…</div>}
            {loaded && shown.length === 0 && (
              <div className="text-xs text-zinc-400" data-testid="migrations-empty">
                {tab === "applied" ? "No migrations applied yet." : "No new migrations — all applied."}
              </div>
            )}
            {shown.map((m) => <MigrationCard key={m.id} m={m} applied={tab === "applied"} onRan={load} />)}
          </>
        )}
      </div>
    </div>
  );
}
